import { Routes, Route, useLocation } from "react-router-dom";
import { auth } from "./API/Auth";
import { store } from "./app/store";
import { useAppDispatch } from "./app/hooks";
import { useEffect, useState, Component } from "react";
import { initializeState, loadState } from "./sessionstorage";
import { addUserPermissions } from "./slices/User/UserSlices";
import routes from "./Configs/Routes";
import TimerManager from "./API/TimerManager";
import { ExpiryModal, Header, SideMenu } from "./Components";
import { RoutesDataType } from "./Interfaces/Config/Routes.interface";
import * as Styles from "./AppStyles";

// ─── Error Boundary ────────────────────────────────────────────────────────────
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    console.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh",
            gap: "1rem",
            fontFamily: "sans-serif",
          }}
        >
          <h2 style={{ color: "#d32f2f" }}>Something went wrong</h2>
          <p style={{ color: "#555", maxWidth: 480, textAlign: "center" }}>
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <button
            style={{
              padding: "0.5rem 1.5rem",
              background: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: "1rem",
            }}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
          <button
            style={{
              padding: "0.5rem 1.5rem",
              background: "transparent",
              color: "#1890ff",
              border: "1px solid #1890ff",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: "1rem",
            }}
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
// ───────────────────────────────────────────────────────────────────────────────

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentPath = location.pathname;

  const [filteredRoutes, setFilteredRoutes] = useState<RoutesDataType[]>([]);
  const [timerInstance, setTimerInstance] = useState<TimerManager | null>(null);

  const [showLogoutWarningModal, setShowLogoutWarningModal] =
    useState<boolean>(false);
  const onHandleShowModal = () => {
    setShowLogoutWarningModal(true);
  };
  const onHandleLogout = async () => {
    await auth.logout();
    sessionStorage.setItem("unauth", "true");
    window.location.href = "/";
  };

  const onHandleLogoutClick = async () => {
    await auth.logout();
    window.location.href = "/";
  };

  useEffect(() => {
    const unsubscribe = !loadState()?.session
      ? store.subscribe(() => {
          initializeState({
            session: store.getState().session,
          });
        })
      : () => {};
    setTimerInstance(
      TimerManager.getInstance(onHandleShowModal, onHandleLogout)
    );
    return () => {
      unsubscribe();
      timerInstance?.clearTimer();
    };
  }, []);

  const getFilteredRoutes = (permissions: any) => {
    const filtered = routes.filter((route: any) => {
      if (route.hasOwnProperty("privileges")) {
        return route.privileges.some((privilege: any) =>
          permissions?.includes(privilege)
        );
      } else {
        return route;
      }
    });
    return filtered;
  };

  useEffect(() => {
    const session = loadState()?.session;
    const privileges = session?.userPermissions || [];
    const accessToken = session?.userAccessToken;

    const timerInstance = TimerManager.getInstance();
    if (
      ["/login", "/workspace"].some((path) => currentPath.includes(path)) ||
      currentPath === "/"
    ) {
      timerInstance?.clearTimer();
    } else {
      timerInstance?.resetTimer();
    }

    if (
      !accessToken &&
      !["/login", "/workspace"].some((path) => currentPath.includes(path)) &&
      currentPath !== "/"
    ) {
      auth.logoutMethods();
      window.location.href = "/";
    }

    let privilegeTypes: string[] = privileges.map((ele: any) => String(ele?.type));

    const isSuperAdmin = privilegeTypes.includes("20");

    if (isSuperAdmin) {
      const allTypes = Array.from({ length: 21 }, (_, i) => String(i));
      const combined = privilegeTypes.concat(allTypes);
      privilegeTypes = combined.filter(
        (item: string, index: number) => combined.indexOf(item) === index
      );
    }

    setFilteredRoutes(getFilteredRoutes(privilegeTypes));
    dispatch(addUserPermissions(privilegeTypes));
  }, [currentPath]);

  return (
    <div>
      <ExpiryModal
        setShowLogoutWarningModal={setShowLogoutWarningModal}
        onHandleLogout={onHandleLogoutClick}
        showLogoutWarningModal={showLogoutWarningModal}
        timerInstance={timerInstance}
      />
      <Routes>
        {filteredRoutes.map((ele: RoutesDataType) => {
          const { element, path, header, sideMenu } = ele;
          return (
            <Route
              key={path}
              element={
                <Styles.Container>
                  {header && <Header />}
                  <Styles.SubContainer>
                    {sideMenu && <SideMenu />}
                    <ErrorBoundary>{element}</ErrorBoundary>
                  </Styles.SubContainer>
                </Styles.Container>
              }
              path={path}
            />
          );
        })}
      </Routes>
    </div>
  );
}
export default App;
